/**
 * Required-activity lines (MapRequiredActivityLine.vue): from each activity that
 * targets a milestone, a 2px elbow line down then across to the milestone, solid
 * when required and dashed otherwise, coloured by the activity's final colour.
 * A violated connection (activity outside the milestone's phase or after it —
 * kept through a force move) turns error red; the dash pattern keeps
 * carrying required vs optional. Operates on the visible activity set from the
 * content frame.
 *
 * When several activities in the same phase/workstream/lane point at the same
 * milestone their elbows would overlap, so the SVG (MapRequiredLines.vue) staggers
 * each one's vertical drop by 3px — `20 + count*3`, `count` being its 1-based rank
 * within that lane group ordered by descending position. We reproduce that here.
 */
import { isConnectionViolatedByDeadline } from '@/utils/helpers/violatedEdges';
import {
  cssVar,
  hexagonStatusFill,
  linkColor,
  modeOf,
} from '../../draw/palette';
import { layoutAnchor } from '../../layout/activityLayout';
import type { SceneContext } from '../../engine/SceneContext';
import type { MapActivity, MapMilestone } from '@/store/ioStore';

/**
 * Stroke colour of the connection line activity → milestone: a violated
 * connection is error red (projects only — template maps keep their colour),
 * otherwise `baseColor`.
 */
export function connectionStrokeColor(
  activity: Pick<MapActivity, 'phase' | 'deadline'>,
  milestone: Pick<MapMilestone, 'phase' | 'deadline'>,
  baseColor: string,
  isTemplate: boolean,
): string {
  return !isTemplate && isConnectionViolatedByDeadline(activity, milestone)
    ? linkColor('underway-red')
    : baseColor;
}

export function drawRequiredLines(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  // 'R' toolbar toggle (linkMilestoneMode, MapRequiredActivityLine `v-if`):
  // 'none' hides every relation line, 'required' shows only required ones, 'all'
  // shows them all (required solid, optional dashed).
  const mode = map.linkMilestoneMode;
  if (mode === 'none') {
    return;
  }
  const activities = scene.content?.visible ?? [];
  const msById = new Map(io.milestones.map((m) => [m.id, m]));
  // Stagger rank per activity, computed over the FULL activity set (not just the
  // visible subset) so an off-screen / hidden activity earlier in a lane group
  // still shifts the ranks of the visible ones — matching MapRequiredLines.
  const counts = staggerCounts(scene);
  ctx.lineWidth = 2;
  for (const activity of activities) {
    if (!activity.milestone) {
      continue;
    }
    // In 'required' mode only solid required lines show (the optional/dashed ones
    // are hidden); 'all' draws both.
    if (mode === 'required' && !activity.required) {
      continue;
    }
    const ms = msById.get(activity.milestone);
    if (!ms) {
      continue;
    }
    // A collapsed group member's elbow starts at its MARKER (the visible
    // stand-in), like the relationship links — `layoutAnchor` resolves both.
    const { x: ax, y: ay } = layoutAnchor(scene, activity);
    const mx = scene.screenX(map.milestonePos(ms));
    // Horizontal-segment Y, stepped down 3px per line in the lane group (SVG
    // `v ${20 + count * 3}`); a lone line is count 1 → 23px below the activity.
    const hy = ay + 20 + (counts.get(activity.id) ?? 1) * 3;

    // Status view recolours the relation line by state/status (SVG
    // MapRequiredActivityLine `data-fill-mode='status'`); otherwise it uses the
    // RESPONSIBLE function's colour (SVG `functionColors(responsible).color`),
    // NOT the activity's final colour.
    const baseColor = map.isHidingActivityColors
      ? hexagonStatusFill(modeOf(activity.state, activity.status))
      : (io.functionColors(activity.responsible).color ??
        cssVar('--color-gray-400', '#98A2B3'));
    // A violated connection is error red in every view mode; the
    // dash pattern below still tells required (solid) from optional (dashed).
    ctx.strokeStyle = connectionStrokeColor(
      activity,
      ms,
      baseColor,
      io.isTemplate,
    );
    // Dim the relation line together with its (filter-shadowed) activity, like
    // the SVG MapRequiredActivityLine `:class="{ shadowed }"`.
    ctx.globalAlpha = map.isShadowedActivityId(activity.id) ? 0.2 : 1;
    ctx.setLineDash(activity.required ? [] : [4, 2]);
    ctx.beginPath();
    // Mirror MapRequiredActivityLine (`m 0,0 v …`): the line starts at the
    // activity centre (the SVG `<g>` is translated to the activity Y), then drops
    // to the staggered level and runs across to the milestone.
    ctx.moveTo(ax, ay + 14);
    ctx.lineTo(ax, hy);
    ctx.lineTo(mx, hy);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

/**
 * Memo for {@link staggerCounts}. The result is a pure function of the activity
 * set and the layout's row assignment, so we key the cache on the identity of
 * `io.activities` (a Pinia getter whose object reference stays stable until its
 * reactive deps change) and the layout view's `epoch` (the lane getter identity
 * in classic mode, a counter bumped on target changes in flow mode). During a
 * redraw that isn't a data/layout change (hover, selection, banner hover) the
 * cache hits and we skip rebuilding the groups over the full activity set.
 */
let staggerMemo: {
  activities: readonly MapActivity[];
  epoch: unknown;
  counts: Map<string, number>;
} | null = null;

/**
 * 1-based rank of each milestone-targeting activity within its
 * phase/workstream/row group, ordered by descending position — the SVG
 * `count` (`index + 1`) that drives the 3px elbow stagger. Computed over all
 * activities so ranks stay stable regardless of which are currently visible.
 * "Row" is the layout's grouping key: the packed lane in classic mode, the
 * quantised fan row in flow mode.
 */
function staggerCounts(scene: SceneContext): Map<string, number> {
  const { map, io } = scene;
  const layout = scene.layout;
  const epoch: unknown = layout?.epoch ?? map.activityLanes;
  if (
    !scene.exporting &&
    staggerMemo &&
    staggerMemo.activities === io.activities &&
    staggerMemo.epoch === epoch
  ) {
    return staggerMemo.counts;
  }

  const groups = new Map<string, MapActivity[]>();

  const withMilestone = io.activities
    .filter((a) => !!a.milestone)
    .sort((a, b) => b.originalPosition - a.originalPosition);
  for (const activity of withMilestone) {
    const row = layout
      ? layout.rowKey(activity.id)
      : (map.activityLanes[activity.id]?.number ?? 0);
    const key = `${activity.phase}-${activity.workstream}-${row}`;
    const group = groups.get(key);
    if (group) {
      group.push(activity);
    } else {
      groups.set(key, [activity]);
    }
  }

  const counts = new Map<string, number>();
  for (const group of groups.values()) {
    group.forEach((activity, index) => counts.set(activity.id, index + 1));
  }

  if (!scene.exporting) {
    staggerMemo = { activities: io.activities, epoch, counts };
  }

  return counts;
}
